// external
import type { FC } from 'react';

// interstate components
import { Box } from '@interstate/components/Box';
import { Typography } from '@interstate/components/Typography';

// components
import { ResidualFormSection } from './ResidualFormSection';
import { ResidualBasedOnField } from './ResidualBasedOnField';
import { ProgramResidualField } from './ProgramResidualField';
import { ResidualTypeToggle } from './ResidualTypeToggle';
import { DollarResidualWarning } from './DollarResidualWarning';

// types
import { RESIDUAL_DOLLAR, RESIDUAL_PERCENT } from '@makemydeal/dr-platform-types';
import type { ResidualBreakdownProps, ResidualFormField } from './mileageCostOptionsTypes';

// constants
import { RESIDUAL_FIELD_LABELS, RESIDUAL_SECTION_TITLE, FIELD_TYPE } from './constants';

// utils
import { formatDecimalPlaces } from '../utils/formatUtils';

// styles
import { ResidualFieldsContainer, SectionTitle, NumericInputContainer } from './MileageCostOptions.style';

/* istanbul ignore next -- helper function for defensive fallback */
const safeFormatDecimalPlaces = (value: number | undefined | null): string => formatDecimalPlaces(value ?? 0) ?? '';

export const ResidualBreakdown: FC<ResidualBreakdownProps> = ({
    currentData,
    errors,
    isLeaseResidualTreatmentEnabled,
    displayedResidualType,
    onResidualTypeChange,
    residualControlsDisabled,
    restrictToDisplayedResidualUnit,
    isUnifiedResidualFieldsEnabled
}) => {
    const dollarNotEditable = !!restrictToDisplayedResidualUnit && displayedResidualType !== RESIDUAL_DOLLAR;
    const percentNotEditable = !!restrictToDisplayedResidualUnit && displayedResidualType !== RESIDUAL_PERCENT;

    // Rows 2-3: Base residual fields and program adjustment (not rendered when unified residual fields is on)
    const topFields: ResidualFormField[] = [
        // Row 2: Base residual (left) | Base residual % (right)
        {
            key: 'baseResidualDollar',
            label: RESIDUAL_FIELD_LABELS.BASE_RESIDUAL,
            type: FIELD_TYPE.CURRENCY,
            inputPrefix: '$',
            value: safeFormatDecimalPlaces(currentData.baseResidualDollar),
            allowDecimal: true,
            readOnly: true
        },
        {
            key: 'baseResidualPercent',
            label: RESIDUAL_FIELD_LABELS.BASE_RESIDUAL_PERCENT,
            type: FIELD_TYPE.NUMERIC,
            inputPrefix: '%',
            value: currentData.baseResidualPercent,
            readOnly: true
        },
        // Row 3: — (left) | Program Adjustment % (right)
        {
            key: 'emptyCell2',
            label: '',
            type: FIELD_TYPE.TEXT,
            value: '—',
            readOnly: true
        },
        {
            key: 'programAdjustmentPercent',
            label: RESIDUAL_FIELD_LABELS.PROGRAM_ADJUSTMENT_PERCENT,
            type: FIELD_TYPE.NUMERIC,
            inputPrefix: '%',
            value: currentData.programAdjustmentPercent,
            readOnly: true
        }
    ];

    // Rows 5-8: Remaining fields after program residual
    const bottomFields: ResidualFormField[] = [
        // Row 5: Existing Mileage Cost (left) | Empty (right)
        {
            key: 'existingMileageCost',
            label: RESIDUAL_FIELD_LABELS.EXISTING_MILEAGE_COST,
            type: FIELD_TYPE.CURRENCY,
            inputPrefix: '$',
            value: safeFormatDecimalPlaces(currentData.existingMileageCost),
            allowDecimal: true,
            readOnly: true
        },
        {
            key: 'emptyCell3',
            label: '',
            type: FIELD_TYPE.TEXT,
            value: '',
            readOnly: true
        },
        // Row 6: Prepaid Mileage Cost (left) | Empty (right)
        {
            key: 'prepaidMileageCost',
            label: RESIDUAL_FIELD_LABELS.PREPAID_MILEAGE_COST,
            type: FIELD_TYPE.CURRENCY,
            inputPrefix: '$',
            value: safeFormatDecimalPlaces(currentData.prepaidMileageCost),
            allowDecimal: true,
            readOnly: true
        },
        {
            key: 'emptyCell4',
            label: '',
            type: FIELD_TYPE.TEXT,
            value: '',
            readOnly: true
        },
        // Row 7: Residualized Accessories (left) | Empty (right)
        {
            key: 'residualizedAccessories',
            label: RESIDUAL_FIELD_LABELS.RESIDUALIZED_ACCESSORIES,
            type: FIELD_TYPE.CURRENCY,
            inputPrefix: '$',
            value: safeFormatDecimalPlaces(currentData.residualizedAccessories),
            allowDecimal: true,
            readOnly: true
        },
        {
            key: 'emptyCell5',
            label: '',
            type: FIELD_TYPE.TEXT,
            value: '',
            readOnly: true
        },
        // Row 8: Adjusted Residual (left) | Empty (right)
        {
            key: 'adjustedResidual',
            label: RESIDUAL_FIELD_LABELS.ADJUSTED_RESIDUAL,
            type: FIELD_TYPE.CURRENCY,
            inputPrefix: '$',
            value: safeFormatDecimalPlaces(currentData.adjustedResidual),
            allowDecimal: true,
            readOnly: true
        },
        {
            key: 'emptyCell6',
            label: '',
            type: FIELD_TYPE.TEXT,
            value: '',
            readOnly: true
        }
    ];

    return (
        <Box sx={{ marginBottom: '2rem' }}>
            <SectionTitle variant="h4" color="primary">
                {RESIDUAL_SECTION_TITLE}
            </SectionTitle>
            {isLeaseResidualTreatmentEnabled && (
                <Box sx={{ marginBottom: '1rem' }} data-testid="dollar-residual-warning-container">
                    <DollarResidualWarning displayedType={displayedResidualType} />
                </Box>
            )}
            <Box sx={{ marginBottom: '2rem' }}>
                <ResidualFieldsContainer>
                    {/* Row 1: Residual Based On (left) with PO call | Residual Type toggle (right) */}
                    <NumericInputContainer data-testid="residual-field-residualBasedOn">
                        <ResidualBasedOnField
                            disabled={residualControlsDisabled}
                            error={errors.residualBasedOn}
                            data-testid="residualBasedOn-input"
                        />
                    </NumericInputContainer>
                    <NumericInputContainer
                        data-testid="residual-field-residualType"
                        style={isLeaseResidualTreatmentEnabled ? { alignSelf: 'stretch' } : undefined}
                    >
                        {isLeaseResidualTreatmentEnabled ? (
                            <Box data-testid="residual-type-controls" sx={{ height: '100%' }}>
                                <ResidualTypeToggle
                                    label={RESIDUAL_FIELD_LABELS.RESIDUAL_TYPE}
                                    selectedType={displayedResidualType}
                                    onChange={onResidualTypeChange}
                                />
                            </Box>
                        ) : (
                            <Typography tag="div" variant="body-md" sx={{ visibility: 'hidden' }}>
                                &nbsp;
                            </Typography>
                        )}
                    </NumericInputContainer>
                </ResidualFieldsContainer>
            </Box>

            {/* Rows 2-3: Base residual fields and program adjustment (not rendered when unified residual fields is on) */}
            {!isUnifiedResidualFieldsEnabled && <ResidualFormSection title="" fields={topFields} />}

            <Box sx={{ marginBottom: '2rem' }}>
                <ResidualFieldsContainer>
                    {/* Row 4: Program Residual $ (left) with PO call | Program Residual % (right) with PO call */}
                    <NumericInputContainer data-testid="residual-field-programResidual">
                        <ProgramResidualField
                            fieldType="dollar"
                            disabled={residualControlsDisabled || dollarNotEditable}
                            error={errors.programResidual}
                            data-testid="programResidual-input"
                        />
                    </NumericInputContainer>
                    <NumericInputContainer data-testid="residual-field-programResidualPercent">
                        <ProgramResidualField
                            fieldType="percent"
                            disabled={residualControlsDisabled || percentNotEditable}
                            error={errors.programResidualPercent}
                            data-testid="programResidualPercent-input"
                        />
                    </NumericInputContainer>
                </ResidualFieldsContainer>
            </Box>

            {/* Rows 5-8: Remaining fields after program residual */}
            <ResidualFormSection title="" fields={bottomFields} />
        </Box>
    );
};

export default ResidualBreakdown;
